// ─────────────────────────────────────────────────────────────────────────────
// stores/apiTokenStore.ts – API-Token (apiTokens) Pinia Store
//
// Verwaltung der Zugriffstoken der oeffentlichen REST-API (REQ-SEC-002,
// REQ-DM-017). Die LISTE wird direkt aus der top-level Collection `apiTokens`
// per onSnapshot gelesen – es gibt bewusst KEINE Listen-Function; die Dokumente
// enthalten nur Hash + Metadaten (kein Klartext) und sind daher sicher direkt
// lesbar (die Sammlung ist in den Rules admin-only, REQ-SEC-029 (a)).
//
// ── ZWEI ABOS, UND ZWAR AUS EINEM INDEX-GRUND (T-M31.4, REQ-SEC-035 (d)) ────
// Seit dem 2026-09-04 gibt es zwei Token-KLASSEN (REQ-SEC-007 Praez. 3):
//   • BETRIEBS-TOKEN — sie tragen eine `companyId` und gelten fuer ihren
//     Mandanten;
//   • GLOBALE TOKEN — sie tragen `provisioning: true` und KEINE `companyId`
//     und gelten fuer alle Mandanten.
// Die Bedienstelle fuehrt beide in EINER Liste. Erhoben werden sie ueber ZWEI
// Abos mit je EINEM Gleichheitsfilter (`companyId == …` und
// `provisioning == true`) — ausdruecklich NICHT ueber eine Abfrage mit
// zusammengesetzter Bedingung und ausdruecklich OHNE `orderBy`: beides braeuchte
// einen zusammengesetzten Index, und der ist ein Auslieferungsschritt mit
// Sichtpruefung im Zielprojekt (REQ-DM-050a, REQ-OPS-039). Zusammengefuehrt und
// nach `createdAt` absteigend sortiert wird deshalb REIN CLIENTSEITIG.
//
// ⚠ EIN TOKEN MIT `provisioning: true` UND EINER `companyId` IST KEIN GLOBALES
//   TOKEN (REQ-SEC-007 Praez. 3 (a)): es ist ein Betriebs-Token seines
//   Mandanten, das zusaetzlich Mandanten anlegen darf. Der globale Zweig filtert
//   es deshalb IM CODE weg — es erscheint beim Mandanten, und zwar genau einmal.
//
// Wie die uebrigen Stores werden die vorigen Subscriptions VOR dem Resubscribe
// abgeraeumt (Company-Switch-Bug-Muster) — BEIDE, denn beim Mandantenwechsel
// bleibt der globale Zweig inhaltlich gleich und wuerde sich sonst still
// verdoppeln (REQ-OPS-053 (d)).
//
// Erzeugen/Widerrufen laufen serverseitig ueber die Cloud Functions
// `createapitoken` / `revokeapitoken` (Firebase-ID-Token als Bearer, wie
// `inviteuser`; Admin-Gate REQ-SEC-031). Der Klartext-Token kommt NUR EINMAL aus
// `createapitoken` zurueck und wird nirgends gespeichert. Der Widerruf wirkt
// fuer BEIDE Klassen gleich und unveraendert (REQ-SEC-035 (e)).
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  collection, query, where,
  type QuerySnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db, callable } from '@/firebase/config';
import { liveAbo } from '@/utils/liveAbo';
import type { ApiToken, CreateApiTokenResult } from '@/firebase/models';

export const useApiTokenStore = defineStore('apiTokens', () => {
  /** Die Token des gewaehlten Mandanten (`companyId == …`). */
  const mandantenTokens = ref<ApiToken[]>([]);
  /** Die GLOBALEN Token (`provisioning == true` UND ohne `companyId`). */
  const globaleTokens   = ref<ApiToken[]>([]);

  const error = ref<string | null>(null);

  /** Je Abo ein Ladezustand — die Liste ist erst fertig, wenn beide da sind. */
  const _mandantLaedt = ref(false);
  const _globalLaedt  = ref(false);
  /** Je Abo ein „lebt"-Merkmal (REQ-OPS-053 (a) (ii), OP-151). */
  const _mandantAktiv = ref(false);
  const _globalAktiv  = ref(false);

  let _unsubMandant: Unsubscribe | null = null;
  let _unsubGlobal:  Unsubscribe | null = null;

  const isLoading = computed(() => _mandantLaedt.value || _globalLaedt.value);

  /**
   * Haelt der Store gerade seine Listener? **Beide** — ein einzelnes totes Abo
   * ist eine unvollstaendige Liste, und die darf sich nicht „live" nennen
   * (REQ-OPS-053 (a) (ii)).
   */
  const isSubscribed = computed(() => _mandantAktiv.value && _globalAktiv.value);

  function _createdMillis(t: ApiToken): number {
    const ts = t.createdAt as unknown as { toMillis?: () => number } | null;
    return ts?.toMillis ? ts.toMillis() : 0;
  }

  /**
   * Die EINE Liste der Bedienstelle: beide Klassen zusammen, neueste zuerst.
   * Die Sortierung steht hier und nicht in der Abfrage — siehe Kopf.
   */
  const tokens = computed<ApiToken[]>(
    () => [...mandantenTokens.value, ...globaleTokens.value]
      .sort((a, b) => _createdMillis(b) - _createdMillis(a)),
  );

  /** Ist dieses Dokument ein GLOBALES Token (REQ-SEC-007 Praez. 3 (a))? */
  function istGlobalesToken(t: ApiToken): boolean {
    return t.provisioning === true && !t.companyId;
  }

  function _raeumeAb(): void {
    _unsubMandant?.();
    _unsubGlobal?.();
    _unsubMandant = null;
    _unsubGlobal  = null;
    _mandantAktiv.value = false;
    _globalAktiv.value  = false;
  }

  function subscribe(companyId: string): void {
    // BEIDE vorigen Subscriptions abraeumen, bevor neu abonniert wird
    // (Company-Switch; der globale Zweig verdoppelte sich sonst still).
    _raeumeAb();
    if (!companyId) {
      mandantenTokens.value = [];
      globaleTokens.value   = [];
      return;
    }
    _mandantLaedt.value = true;
    _globalLaedt.value  = true;
    error.value         = null;

    // ── Zweig 1: die Token des Mandanten ──────────────────────────────────
    // Nur companyId-Filter -> kein Composite-Index noetig; Sortierung im Code.
    const qMandant = query(
      collection(db, 'apiTokens'),
      where('companyId', '==', companyId),
    );
    _mandantAktiv.value = true;
    // Fehlerweg, Protokoll und Rueckzugsstaffel kommen aus der EINEN
    // Bildungsstelle (REQ-OPS-053 (e), T-M21.4).
    _unsubMandant = liveAbo<QuerySnapshot>({
      collection: 'apiTokens',
      companyId,
      ziel: () => qMandant,
      onData: (snap) => {
        mandantenTokens.value = snap.docs.map(d => ({ id: d.id, ...d.data() } as ApiToken));
        _mandantLaedt.value   = false;
      },
      onFehler: (err) => {
        error.value         = err.message;
        _mandantLaedt.value = false;
        _mandantAktiv.value = false;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung).
      onErholt: () => {
        error.value         = null;
        _mandantAktiv.value = true;
      },
      onStill: () => {
        _mandantLaedt.value = false;
        _mandantAktiv.value = false;
      },
    });

    // ── Zweig 2: die GLOBALEN Token ───────────────────────────────────────
    // EIN Gleichheitsfilter, KEIN `orderBy` -> der automatische Einzelfeld-Index
    // genuegt (REQ-DM-050a). Er traegt bewusst KEINEN Mandantenfilter: ein
    // globales Token gehoert keinem Mandanten (REQ-DM-017 Praez. 1).
    const qGlobal = query(
      collection(db, 'apiTokens'),
      where('provisioning', '==', true),
    );
    _globalAktiv.value = true;
    _unsubGlobal = liveAbo<QuerySnapshot>({
      collection: 'apiTokens',
      ziel: () => qGlobal,
      onData: (snap) => {
        globaleTokens.value = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as ApiToken))
          // Die Abfrage kann `provisioning`-Token MIT Mandanten mitbringen;
          // die gehoeren in den Mandanten-Zweig und sind hier auszusortieren.
          .filter(istGlobalesToken);
        _globalLaedt.value = false;
      },
      onFehler: (err) => {
        error.value        = err.message;
        _globalLaedt.value = false;
        _globalAktiv.value = false;
      },
      onErholt: () => {
        error.value        = null;
        _globalAktiv.value = true;
      },
      onStill: () => {
        _globalLaedt.value = false;
        _globalAktiv.value = false;
      },
    });
  }

  function unsubscribe(): void {
    _raeumeAb();
    mandantenTokens.value = [];
    globaleTokens.value   = [];
    error.value           = null;
    _mandantLaedt.value   = false;
    _globalLaedt.value    = false;
  }

  // ── Erzeugen (REQ-SEC-002, REQ-SEC-035 (b), REQ-DM-017 Praez. 1 (b)) ───────
  // Genau EINES von beidem geht hinaus: `global: true` ODER eine `companyId`.
  // Beides zugleich und keines von beidem wiese die Cloud mit 400 ab — die
  // Oberflaeche erreicht diesen Fall gar nicht erst (T-M31.4, Kriterium 3).
  // Der zurueckgegebene Klartext (`token`) ist NUR EINMAL sichtbar.
  async function createToken(opts: {
    companyId?: string;
    /** Wahrheitswert; **allein `true` wirkt** (REQ-DM-017 Praez. 1 (b)). */
    global?:    boolean;
    name?:      string;
    expiresAt?: string;   // ISO-8601, Zukunft
  }): Promise<CreateApiTokenResult> {
    const globalesToken = opts.global === true;
    return callable<CreateApiTokenResult>('createapitoken', {
      // Bei einem globalen Token geht KEIN Feld `companyId` hinaus — nicht ein
      // leeres und nicht `null`. Die Antwort fuehrt `companyId: null`.
      ...(globalesToken ? { global: true } : { companyId: opts.companyId }),
      ...(opts.name       ? { name: opts.name }             : {}),
      ...(opts.expiresAt  ? { expiresAt: opts.expiresAt }   : {}),
    });
  }

  // ── Widerrufen (irreversibel; fuer BEIDE Klassen derselbe Weg) ─────────────
  async function revokeToken(id: string): Promise<void> {
    await callable('revokeapitoken', { id });
  }

  return {
    tokens, mandantenTokens, globaleTokens,
    isLoading, error, isSubscribed,
    subscribe, unsubscribe,
    createToken, revokeToken,
    istGlobalesToken,
  };
});
